import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ApiKeysTab() {
  const [activeProvider, setActiveProvider] = useState('stripe');
  const [stripeKeys, setStripeKeys] = useState({
    secret_key: '',
    webhook_secret: ''
  });
  const [paypalKeys, setPaypalKeys] = useState({
    client_id: '',
    client_secret: ''
  });
  const [stripePrices, setStripePrices] = useState({});
  const [paypalPlans, setPaypalPlans] = useState({});
  const [environment, setEnvironment] = useState('test');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    loadStripePrices();
    loadPayPalPlans();
    loadAuditLogs();
  }, [environment]);

  const loadStripePrices = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/stripe/prices?environment=${environment}`
      );
      // Convert array to object for easier lookup
      const pricesObj = {};
      response.data.data.forEach(price => {
        pricesObj[`${price.plan_id}_${price.billing_cycle}`] = price.stripe_price_id;
      });
      setStripePrices(pricesObj);
    } catch (error) {
      console.error('Error loading Stripe prices:', error);
    }
  };

  const loadPayPalPlans = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/paypal/plans?environment=${environment}`
      );
      const plansObj = {};
      response.data.data.forEach(plan => {
        plansObj[`${plan.plan_id}_${plan.billing_cycle}`] = plan.paypal_plan_id;
      });
      setPaypalPlans(plansObj);
    } catch (error) {
      console.error('Error loading PayPal plans:', error);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/api-keys/audit/log?limit=10`
      );
      setAuditLogs(response.data.data);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };

  const handleSaveStripeKeys = async () => {
    setSaving(true);
    try {
      // Save secret key
      if (stripeKeys.secret_key) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/api-keys`, {
          provider: 'stripe',
          key_type: 'secret_key',
          key_value: stripeKeys.secret_key,
          environment
        });
      }

      // Save webhook secret
      if (stripeKeys.webhook_secret) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/api-keys`, {
          provider: 'stripe',
          key_type: 'webhook_secret',
          key_value: stripeKeys.webhook_secret,
          environment
        });
      }

      alert('Stripe keys saved successfully!');
      setStripeKeys({ secret_key: '', webhook_secret: '' });
      loadAuditLogs();
    } catch (error) {
      console.error('Error saving Stripe keys:', error);
      alert('Error saving Stripe keys: ' + error.response?.data?.error);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayPalKeys = async () => {
    setSaving(true);
    try {
      // Save client ID
      if (paypalKeys.client_id) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/api-keys`, {
          provider: 'paypal',
          key_type: 'client_id',
          key_value: paypalKeys.client_id,
          environment
        });
      }

      // Save client secret
      if (paypalKeys.client_secret) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/api-keys`, {
          provider: 'paypal',
          key_type: 'client_secret',
          key_value: paypalKeys.client_secret,
          environment
        });
      }

      alert('PayPal keys saved successfully!');
      setPaypalKeys({ client_id: '', client_secret: '' });
      loadAuditLogs();
    } catch (error) {
      console.error('Error saving PayPal keys:', error);
      alert('Error saving PayPal keys: ' + error.response?.data?.error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStripePrices = async () => {
    setSaving(true);
    try {
      const plans = ['STARTER', 'PROFESSIONAL', 'AGENCY'];
      const cycles = ['monthly', 'yearly'];

      for (const plan of plans) {
        for (const cycle of cycles) {
          const key = `${plan}_${cycle}`;
          if (stripePrices[key]) {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/stripe/prices`, {
              plan_id: plan,
              billing_cycle: cycle,
              price_id: stripePrices[key],
              environment
            });
          }
        }
      }

      alert('Stripe prices saved successfully!');
      loadStripePrices();
      loadAuditLogs();
    } catch (error) {
      console.error('Error saving Stripe prices:', error);
      alert('Error saving Stripe prices: ' + error.response?.data?.error);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayPalPlans = async () => {
    setSaving(true);
    try {
      const plans = ['STARTER', 'PROFESSIONAL', 'AGENCY'];
      const cycles = ['monthly', 'yearly'];

      for (const plan of plans) {
        for (const cycle of cycles) {
          const key = `${plan}_${cycle}`;
          if (paypalPlans[key]) {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/paypal/plans`, {
              plan_id: plan,
              billing_cycle: cycle,
              paypal_plan_id: paypalPlans[key],
              environment
            });
          }
        }
      }

      alert('PayPal plans saved successfully!');
      loadPayPalPlans();
      loadAuditLogs();
    } catch (error) {
      console.error('Error saving PayPal plans:', error);
      alert('Error saving PayPal plans: ' + error.response?.data?.error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider) => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/${provider}/test?environment=${environment}`
      );
      setTestResult({ success: true, message: response.data.message });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Environment Selector */}
      <div style={{ marginBottom: '30px', display: 'flex', gap: '20px', alignItems: 'center' }}>
        <label style={{ fontWeight: 'bold' }}>Environment:</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          style={{
            padding: '10px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '1rem'
          }}
        >
          <option value="test">Test / Sandbox</option>
          <option value="production">Production</option>
        </select>
        <span style={{
          padding: '8px 16px',
          borderRadius: '20px',
          background: environment === 'production' ? '#ffc107' : '#17a2b8',
          color: 'white',
          fontSize: '0.9rem',
          fontWeight: 'bold'
        }}>
          {environment === 'production' ? '🔴 LIVE' : '🧪 TEST'}
        </span>
      </div>

      {/* Provider Tabs */}
      <div style={{ marginBottom: '20px', borderBottom: '2px solid #dee2e6' }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          <button
            onClick={() => setActiveProvider('stripe')}
            style={{
              padding: '15px 30px',
              background: activeProvider === 'stripe' ? '#667eea' : 'transparent',
              color: activeProvider === 'stripe' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeProvider === 'stripe' ? '3px solid #667eea' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            💳 Stripe
          </button>
          <button
            onClick={() => setActiveProvider('paypal')}
            style={{
              padding: '15px 30px',
              background: activeProvider === 'paypal' ? '#667eea' : 'transparent',
              color: activeProvider === 'paypal' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeProvider === 'paypal' ? '3px solid #667eea' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            💰 PayPal
          </button>
          <button
            onClick={() => setActiveProvider('audit')}
            style={{
              padding: '15px 30px',
              background: activeProvider === 'audit' ? '#667eea' : 'transparent',
              color: activeProvider === 'audit' ? 'white' : '#666',
              border: 'none',
              borderBottom: activeProvider === 'audit' ? '3px solid #667eea' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            📋 Audit Log
          </button>
        </div>
      </div>

      {/* Stripe Configuration */}
      {activeProvider === 'stripe' && (
        <div>
          <h2>Stripe API Configuration</h2>

          {/* API Keys Section */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <h3>API Keys</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Secret Key (sk_test_... or sk_live_...)
              </label>
              <input
                type="password"
                value={stripeKeys.secret_key}
                onChange={(e) => setStripeKeys({ ...stripeKeys, secret_key: e.target.value })}
                placeholder={environment === 'production' ? 'sk_live_...' : 'sk_test_...'}
                style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Webhook Secret (whsec_...)
              </label>
              <input
                type="password"
                value={stripeKeys.webhook_secret}
                onChange={(e) => setStripeKeys({ ...stripeKeys, webhook_secret: e.target.value })}
                placeholder="whsec_..."
                style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
              />
            </div>

            <button
              onClick={handleSaveStripeKeys}
              disabled={saving || (!stripeKeys.secret_key && !stripeKeys.webhook_secret)}
              style={{
                padding: '12px 24px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginRight: '10px'
              }}
            >
              {saving ? 'Saving...' : 'Save API Keys'}
            </button>

            <button
              onClick={() => handleTestConnection('stripe')}
              disabled={testing}
              style={{
                padding: '12px 24px',
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {testResult && (
              <div style={{
                marginTop: '15px',
                padding: '12px',
                borderRadius: '6px',
                background: testResult.success ? '#d4edda' : '#f8d7da',
                color: testResult.success ? '#155724' : '#721c24',
                border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>

          {/* Price IDs Section */}
          <div className="card">
            <h3>Price IDs</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Configure Stripe Price IDs for each plan and billing cycle.
            </p>

            {['STARTER', 'PROFESSIONAL', 'AGENCY'].map(plan => (
              <div key={plan} style={{ marginBottom: '30px' }}>
                <h4 style={{ borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>
                  {plan.charAt(0) + plan.slice(1).toLowerCase()} Plan
                </h4>
                {['monthly', 'yearly'].map(cycle => (
                  <div key={cycle} style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      {cycle.charAt(0).toUpperCase() + cycle.slice(1)} Price ID:
                    </label>
                    <input
                      type="text"
                      value={stripePrices[`${plan}_${cycle}`] || ''}
                      onChange={(e) => setStripePrices({
                        ...stripePrices,
                        [`${plan}_${cycle}`]: e.target.value
                      })}
                      placeholder="price_..."
                      style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
                    />
                  </div>
                ))}
              </div>
            ))}

            <button
              onClick={handleSaveStripePrices}
              disabled={saving}
              style={{
                padding: '12px 24px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {saving ? 'Saving...' : 'Save Price IDs'}
            </button>
          </div>
        </div>
      )}

      {/* PayPal Configuration */}
      {activeProvider === 'paypal' && (
        <div>
          <h2>PayPal API Configuration</h2>

          {/* API Keys Section */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <h3>API Credentials</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Client ID
              </label>
              <input
                type="text"
                value={paypalKeys.client_id}
                onChange={(e) => setPaypalKeys({ ...paypalKeys, client_id: e.target.value })}
                placeholder="A..."
                style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Client Secret
              </label>
              <input
                type="password"
                value={paypalKeys.client_secret}
                onChange={(e) => setPaypalKeys({ ...paypalKeys, client_secret: e.target.value })}
                placeholder="E..."
                style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
              />
            </div>

            <button
              onClick={handleSavePayPalKeys}
              disabled={saving || (!paypalKeys.client_id && !paypalKeys.client_secret)}
              style={{
                padding: '12px 24px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginRight: '10px'
              }}
            >
              {saving ? 'Saving...' : 'Save Credentials'}
            </button>

            <button
              onClick={() => handleTestConnection('paypal')}
              disabled={testing}
              style={{
                padding: '12px 24px',
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {testResult && (
              <div style={{
                marginTop: '15px',
                padding: '12px',
                borderRadius: '6px',
                background: testResult.success ? '#d4edda' : '#f8d7da',
                color: testResult.success ? '#155724' : '#721c24',
                border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>

          {/* Plan IDs Section */}
          <div className="card">
            <h3>Billing Plan IDs</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Configure PayPal Billing Plan IDs for each plan and billing cycle.
            </p>

            {['STARTER', 'PROFESSIONAL', 'AGENCY'].map(plan => (
              <div key={plan} style={{ marginBottom: '30px' }}>
                <h4 style={{ borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>
                  {plan.charAt(0) + plan.slice(1).toLowerCase()} Plan
                </h4>
                {['monthly', 'yearly'].map(cycle => (
                  <div key={cycle} style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      {cycle.charAt(0).toUpperCase() + cycle.slice(1)} Plan ID:
                    </label>
                    <input
                      type="text"
                      value={paypalPlans[`${plan}_${cycle}`] || ''}
                      onChange={(e) => setPaypalPlans({
                        ...paypalPlans,
                        [`${plan}_${cycle}`]: e.target.value
                      })}
                      placeholder="P-..."
                      style={{ width: '100%', padding: '10px', fontSize: '1rem' }}
                    />
                  </div>
                ))}
              </div>
            ))}

            <button
              onClick={handleSavePayPalPlans}
              disabled={saving}
              style={{
                padding: '12px 24px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {saving ? 'Saving...' : 'Save Plan IDs'}
            </button>
          </div>
        </div>
      )}

      {/* Audit Log */}
      {activeProvider === 'audit' && (
        <div className="card">
          <h2>API Key Audit Log</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Recent API key management activities
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Provider</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Key Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Changed By</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No audit logs yet
                  </td>
                </tr>
              ) : (
                auditLogs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: log.provider === 'stripe' ? '#e3f2fd' : '#fff3cd',
                        color: log.provider === 'stripe' ? '#0277bd' : '#856404'
                      }}>
                        {log.provider}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontFamily: 'monospace' }}>
                      {log.key_type}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: log.action === 'created' || log.action === 'updated' ? '#d4edda' : '#f8d7da',
                        color: log.action === 'created' || log.action === 'updated' ? '#155724' : '#721c24'
                      }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {log.changed_by_name || log.changed_by_email || 'System'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ApiKeysTab;
